import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import PersonalInfo from "@/components/profile/PersonalInfo";
import AboutSection from "@/components/profile/AboutSection";
import SocialLinks from "@/components/profile/SocialLinks";
import DesktopLayout from "@/components/layout/DesktopLayout";
import Hey from "@/lib/hey";
import PasswordSection from "@/components/profile/PasswordSection";
import { useFlutterBridge } from "@/hooks/useFlutterBridge";
import { useAnalytics } from "@/hooks/use-analytics";
import DeleteAccount from "@/components/profile/DeleteAccount";
import FeedbackSection from "@/components/profile/FeedbackSection";
const Profile = () => {
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const {
    trackPageView,
    trackProfileAction,
    trackError
  } = useAnalytics();
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    language: "",
    currency: "USD",
    notifications_enabled: true,
    city: "",
    country: ""
  });
  useEffect(() => {
    trackPageView('/profile');
    trackProfileAction('view_profile', 'success', 'page_loaded');
    getProfile();
  }, []);

  // useEffect(() => {
  //   if (!Hey.HeyUtils.getInstance().isUserAuthenticated()) {
  //     return navigate("/auth");
  //   }
  // }, [navigate]);

  const getProfile = async () => {
    try {
      if (!Hey.HeyUtils.getInstance().isUserAuthenticated()) {
        return navigate("/auth");
      }
      console.debug("🔍 DEBUG: Starting profile fetch process");
      trackProfileAction('view_profile', 'success', 'fetch_profile_started');
      console.debug("📤 API REQUEST: getUserProfile");
      const user = await Hey.HeyUtils.getInstance().getUserProfile();
      console.debug("📥 API RESPONSE: getUserProfile", JSON.stringify(user, null, 2));
      console.debug("🔍 DEBUG: Raw user profile data:", JSON.stringify(user, null, 2));
      console.debug("🔍 DEBUG: User profile type:", typeof user);
      if (!user) {
        console.error("🚨 ERROR: Profile not found - user object is null or undefined");
        trackProfileAction('view_profile', 'failed', 'profile_not_found');
        trackError('data_loading', 'Profile not found - user object is null or undefined', 'Profile');
        throw new Error("Profile not found");
      }
      const hasNestedStructure = !!user.user;
      console.debug("🔍 DEBUG: Has nested user structure:", hasNestedStructure);
      if (hasNestedStructure) {
        console.debug("🔍 DEBUG: User.user properties:", Object.keys(user.user || {}));
        const userLanguage = user.user?.language;
        if (userLanguage) {
          console.debug("🔍 DEBUG: Found language in user.user:", userLanguage);
          await i18n.changeLanguage(userLanguage);
          trackProfileAction('view_profile', 'success', `language_detected_${userLanguage}`);
        } else {
          console.debug("⚠️ WARNING: No language found in user.user");
          trackProfileAction('view_profile', 'failed', 'no_language_found');
        }
        if (user.user?.address) {
          console.debug("🔍 DEBUG: Address structure:", user.user.address);
        } else {
          console.debug("⚠️ WARNING: No address found in user.user");
          trackProfileAction('view_profile', 'failed', 'no_address_found');
        }
        setProfile({
          first_name: user.user?.firstName || "",
          last_name: user.user?.lastName || "",
          email: user.user?.email || "",
          language: user.user?.language || i18n.language || "en",
          currency: user.user?.currency || "USD",
          notifications_enabled: true,
          city: user.user?.address?.cityName || "",
          country: user.user?.address?.countryName || ""
        });

        // Track profile completeness
        trackProfileAction('view_profile', 'success', `profile_completeness_${getProfileCompleteness(user.user)}`);
      } else {
        console.debug("🔍 DEBUG: User direct properties:", Object.keys(user));
        const userLanguage = (user as any).language;
        if (userLanguage) {
          console.debug("🔍 DEBUG: Found language directly on user:", userLanguage);
          await i18n.changeLanguage(userLanguage);
        } else {
          console.debug("⚠️ WARNING: No language found directly on user");
        }
        setProfile({
          first_name: (user as any).firstName || "",
          last_name: (user as any).lastName || "",
          email: (user as any).email || "",
          language: (user as any).language || i18n.language || "en",
          currency: (user as any).currency || "USD",
          notifications_enabled: true,
          city: (user as any).address?.cityName || "",
          country: (user as any).address?.countryName || ""
        });
      }
      console.debug("✅ DEBUG: Final profile state being set:", profile);
      trackProfileAction('view_profile', 'success', 'profile_loaded');
    } catch (error) {
      console.error("🚨 ERROR: Error fetching profile:", error);
      console.error("🚨 ERROR: Error details:", {
        message: error.message,
        stack: error.stack
      });
      trackProfileAction('view_profile', 'failed', error.message);
      trackError('data_loading', `Error fetching profile: ${error.message}`, 'Profile');
      toast({
        variant: "destructive",
        title: "Error fetching profile",
        description: "Please try again later."
      });
      setTimeout(() => {
        navigate("/auth");
      }, 1500);
    }
  };
  const getProfileCompleteness = (userProfile: any) => {
    if (!userProfile) return 'empty';
    let filledFields = 0;
    let totalFields = 4; // firstName, lastName, language, address

    if (userProfile.firstName) filledFields++;
    if (userProfile.lastName) filledFields++;
    if (userProfile.language) filledFields++;

    // Check address fields
    if (userProfile.address && (userProfile.address.cityName || userProfile.address.countryName)) {
      filledFields++;
    }
    const percentage = Math.floor(filledFields / totalFields * 100);
    if (percentage === 100) return 'complete';
    if (percentage >= 75) return 'mostly_complete';
    if (percentage >= 50) return 'half_complete';
    if (percentage > 0) return 'minimal';
    return 'empty';
  };
  const updateProfile = async () => {
    try {
      setLoading(true);
      console.debug("🔄 DEBUG: Starting profile update process");
      console.debug("🔄 DEBUG: Profile data being sent:", profile);

      // Track detailed profile update event
      const profileUpdateData = {
        has_name: !!profile.first_name,
        has_location: !!(profile.city && profile.country),
        language: profile.language,
        currency: profile.currency
      };
      trackProfileAction('update_profile', 'success', JSON.stringify(profileUpdateData));
      console.debug("📤 API REQUEST: getUserProfile (pre-update)");
      let user = await Hey.HeyUtils.getInstance().getUserProfile();
      console.debug("📥 API RESPONSE: getUserProfile (pre-update)", JSON.stringify(user, null, 2));
      if (!user) throw new Error("No user found");
      console.debug("🔄 DEBUG: Preparing update payload");
      const updatePayload = {
        user: {
          id: user.user ? user.user.id : (user as any).id,
          firstName: profile.first_name,
          lastName: profile.last_name,
          email: profile.email,
          address: {
            addressLine: "Street 1",
            cityName: profile.city,
            countryName: profile.country
          },
          language: profile.language,
          currency: profile.currency
        }
      };
      console.debug("📤 API REQUEST: updateUser", JSON.stringify(updatePayload, null, 2));
      trackProfileAction('update_profile', 'success', 'api_call_started');
      let isUpdated = await Hey.HeyUtils.getInstance().updateUser(updatePayload);
      console.debug("📥 API RESPONSE: updateUser", isUpdated);
      if (!isUpdated) {
        trackProfileAction('update_profile', 'failed', 'api_returned_false');
        throw new Error("Profile not updated");
      }
      await i18n.changeLanguage(profile.language);
      trackProfileAction('update_profile', 'success', `language_changed_${profile.language}`);
      toast({
        title: t("common.success"),
        description: t("settings.profileUpdated")
      });
      trackProfileAction('update_profile', 'success', 'completed');
    } catch (error) {
      console.error("🚨 ERROR: Error updating profile:", error);
      console.error("🚨 ERROR: Error details:", {
        message: error.message,
        stack: error.stack
      });
      trackProfileAction('update_profile', 'failed', error.message);
      trackError('api', `Error updating profile: ${error.message}`, 'Profile');
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("settings.updateError") + ": " + error.message
      });
    } finally {
      setLoading(false);
    }
  };
  const handleLanguageChange = async (value: string) => {
    console.debug("🌍 DEBUG: Language changed to:", value);
    trackProfileAction('change_language', 'success', value);
    setProfile({
      ...profile,
      language: value
    });
    await i18n.changeLanguage(value);
  };
  const handleCurrencyChange = (value: string) => {
    console.debug("💰 DEBUG: Currency changed to:", value);
    trackProfileAction('change_currency', 'success', value);
    setProfile({
      ...profile,
      currency: value
    });
  };
  useFlutterBridge(null);
  return <DesktopLayout>
      <div className={`container px-4 py-6 ${isMobile ? 'pb-24' : ''}`}>
        <Card className="my-[30px]">
          <CardHeader className="my-0">
            <CardTitle>{t("settings.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <PersonalInfo firstName={profile.first_name} lastName={profile.last_name} email={profile.email} city={profile.city} country={profile.country} language={profile.language} currency={profile.currency} loading={loading} onFirstNameChange={value => {
            trackProfileAction('update_name', 'success', 'first_name');
            setProfile({
              ...profile,
              first_name: value
            });
          }} onLastNameChange={value => {
            trackProfileAction('update_name', 'success', 'last_name');
            setProfile({
              ...profile,
              last_name: value
            });
          }} onCityChange={value => {
            trackProfileAction('update_location', 'success', 'city');
            setProfile({
              ...profile,
              city: value
            });
          }} onCountryChange={value => {
            trackProfileAction('update_location', 'success', 'country');
            setProfile({
              ...profile,
              country: value
            });
          }} onLanguageChange={handleLanguageChange} onCurrencyChange={handleCurrencyChange} onSave={updateProfile} onToggleSection={isOpen => {
            trackProfileAction(isOpen ? 'expand_section' : 'collapse_section', 'success', 'personal_info');
          }} />

            <Separator />

            <PasswordSection onToggleSection={isOpen => {
            trackProfileAction(isOpen ? 'expand_section' : 'collapse_section', 'success', 'password');
          }} />

            <Separator />

            {isMobile && <>
                <FeedbackSection />
                <Separator />
              </>}

            <AboutSection />

            <Separator />

            <SocialLinks />

            <Separator />

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                {t("settings.consent.title")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("settings.consent.description")}
              </p>
              <div className="flex space-x-4">
                <button onClick={() => {
                trackProfileAction('consent_settings', 'success', 'opened_consent_dialog');
                var otBanner = document.getElementById("onetrust-consent-sdk");
                if (otBanner) {
                  otBanner.style.display = "block";
                }
                var buttononetrust = document.getElementsByClassName('ot-floating-button__open');
                if (buttononetrust && buttononetrust.length > 0) {
                  (buttononetrust[0] as HTMLElement).click();
                  var otBanner = document.getElementById("onetrust-banner-sdk");
                  if (otBanner) {
                    otBanner.style.display = "none";
                  }
                  let closeButton = document.getElementById("close-pc-btn-handler");
                  if (closeButton) {
                    closeButton.onclick = function () {
                      trackProfileAction('consent_settings', 'success', 'closed_consent_dialog');
                      var otBanner = document.getElementById("onetrust-consent-sdk");
                      if (otBanner) {
                        otBanner.style.display = "none";
                      }
                    };
                  }
                  let closeButton2 = document.getElementsByClassName("ot-floating-button__close");
                  if (closeButton2 && closeButton2.length > 0) {
                    (closeButton2[0] as HTMLElement).onclick = function () {
                      trackProfileAction('consent_settings', 'success', 'closed_consent_dialog_floating');
                      var otBanner = document.getElementById("onetrust-consent-sdk");
                      if (otBanner) {
                        otBanner.style.display = "none";
                      }
                    };
                  }
                } else {
                  console.error("One Trust Button not found");
                  trackProfileAction('consent_settings', 'failed', 'onetrust_button_not_found');
                }
              }} className="px-4 py-2 text-white rounded bg-[#000835]">
                  {t("settings.consent.open")}
                </button>
              </div>
            </div>

            <Separator />

            <DeleteAccount />

          </CardContent>
        </Card>
      </div>
    </DesktopLayout>;
};
export default Profile;